import { uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Control, FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { SendEmailData, sendEmail } from '~/data/company';
import { EmployeesIndexed, useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import { OrganizationUnit } from '~/data/orgunits/types';
import { useVisible } from '~/hooks/useVisible';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';

type EmailForm = {
  body: string;
  subject: string;
  employees?: number[];
  units?: number[];
  emails?: string;
};

export type Props = {
  open: boolean;
  onClose?: () => void;
};

export type UseSendEmailComponentOutput = {
  allEmployees: EmployeesIndexed;
  control: Control<EmailForm>;
  errorMessage: string;
  loading: boolean;
  orgUnits: OrganizationUnit[];
  recipientsCount: number;
  recipientsConfigVisible: boolean;
  errorDialogVisible: boolean;
  toggleRecipientsVisibility: () => void;
  onErrorDialogClose: (event?: object, reason?: string) => void;
  onDialogClose: (event?: object, reason?: string) => void;
  onSubmit: (e: FormEvent) => void;
  updateRecipientsCount: () => void;
};

const defaultValues = {
  subject: '',
  body: '',
  emails: '',
  units: [],
  employees: [],
};

const getEmailList = (rawEmails: string | undefined) =>
  rawEmails ? rawEmails.replace(/\s/, '').split(',') : [];

const mapPostData = (data: EmailForm): SendEmailData => {
  const additionalEmails = getEmailList(data.emails).map((email: string) => {
    return { address: email };
  });

  return {
    subject: data.subject,
    body: data.body,
    to: {
      employees: data.employees || [],
      units: data.units || [],
      emails: additionalEmails,
    },
  };
};

const getFirstErrorMessage = (errors: FieldErrors<EmailForm>): string => {
  return (
    errors.subject?.message ||
    errors.body?.message ||
    errors.emails?.message ||
    ''
  );
};

export const useSendEmailComponent = ({
  onClose,
}: Props): UseSendEmailComponentOutput => {
  const [recipientsCount, setRecipientCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    isVisible: recipientsConfigVisible,
    hide: hideRecipientVisibility,
    toggle: toggleRecipientsVisibility,
  } = useVisible();
  const {
    isVisible: errorDialogVisible,
    hide: hideErrorDialog,
    show: showErrorDialog,
  } = useVisible();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { allEmployees, isLoading: employeesLoading } = useAllEmployees();

  const { orgUnits, isLoading: orgUnitsLoading } = useAllOrgUnits();

  const validationSchema = useMemo(() => {
    return yup.object().shape<EmailForm>({
      subject: yup.string().required(t('laFillSubBody')),
      body: yup.string().required(t('laFillSubBody')),
      emails: yup
        .string()
        .notRequired()
        .test('emails', t('laInvalidEmailList'), (value) => {
          if (!value) {
            return true;
          }

          return value
            .split(',')
            .every((potentialEmail) =>
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(potentialEmail),
            );
        }),
      units: yup.array<number>().notRequired(),
      employees: yup.array<number>().notRequired(),
    });
  }, [t]);

  const resolver = useYupValidationResolver(validationSchema);
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
  } = useForm({
    resolver,
    defaultValues,
  });

  useEffect(() => {
    register('body');
  }, [register]);

  useEffect(() => {
    const currentError = getFirstErrorMessage(errors);
    if (currentError) {
      setErrorMessage(currentError);
      showErrorDialog();
    }
  }, [errors, showErrorDialog]);

  const onDialogClose = (_event?: object, reason?: string) => {
    if (reason && ['backdropClick', 'escapeKeyDown'].includes(reason)) {
      return;
    }

    if (onClose) {
      onClose();
    }

    setRecipientCount(0);
    hideRecipientVisibility();
  };

  const onErrorDialogClose = (_event?: object, reason?: string) => {
    if (reason && ['backdropClick', 'escapeKeyDown'].includes(reason)) {
      return;
    }

    hideErrorDialog();
    setErrorMessage('');
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!recipientsCount) {
        setErrorMessage(`${t('laOneRecipientNeeded')}`);
        showErrorDialog();

        return;
      }

      const emailData = mapPostData(data);
      const response = await sendEmail(emailData);

      if (response) {
        onDialogClose();
        enqueueSnackbar(t('laEmailSendSuc'), {
          variant: 'success',
        });
      }
    } catch (e) {
      enqueueSnackbar(t('errorMsg'), {
        variant: 'error',
      });
    }
  });

  const updateRecipientsCount = () => {
    const { units, employees, emails } = getValues();
    const emailList = getEmailList(emails);
    const empIds = Object.keys(allEmployees).filter((empId) => {
      const parsedEmpId = parseInt(empId, 10);
      return (
        units?.includes(allEmployees[parsedEmpId].fEmpUnit) ||
        employees?.includes(allEmployees[parsedEmpId].fEmpId)
      );
    });

    setRecipientCount(uniq(empIds).length + uniq(emailList).length);
  };

  return {
    orgUnits,
    allEmployees,
    errorMessage,
    control,
    loading: employeesLoading || orgUnitsLoading,
    recipientsCount,
    recipientsConfigVisible,
    errorDialogVisible,
    onDialogClose,
    onErrorDialogClose,
    onSubmit,
    toggleRecipientsVisibility,
    updateRecipientsCount,
  };
};
